<template>
  <div class="main-panel">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goMainPanel" />
    </div>

    <header class="header">
      <div class="header__left">
        <div class="avatar" aria-hidden="true">✦</div>
        <div>
        <h1>Ayuda · Reportes</h1>
        <p class="sub">Crea un reporte y consulta el seguimiento en Mis reportes</p>
        </div>
      </div>
      <div class="header__right">
        <span class="hero-chip">Flujo de seguimiento</span>
        <button class="btn btn--ghost" @click="goBack">Regresar</button>
      </div>
    </header>

    <div class="tabs">
      <button class="tab" :class="{ 'tab--active': activeTab === 'form' }" @click="activeTab = 'form'">Nuevo reporte</button>
      <button class="tab" :class="{ 'tab--active': activeTab === 'mine' }" @click="switchToMine">Mis reportes</button>
    </div>

    <section v-if="activeTab === 'form'" class="card card--form">
      <div class="form-grid">
        <label class="field">
          <span>Usuario</span>
          <input :value="username" type="text" disabled />
          <small class="hint">Se captura automaticamente de tu sesion.</small>
        </label>

        <label class="field">
          <span>Fecha y hora del error</span>
          <input v-model="form.event_timestamp" type="datetime-local" :class="{ 'input--error': fieldErrors.event_timestamp }" />
          <small class="hint">Indica el momento del incidente.</small>
          <small v-if="fieldErrors.event_timestamp" class="field-error">{{ fieldErrors.event_timestamp }}</small>
        </label>

        <label class="field">
          <span>URL</span>
          <input v-model="form.target_url" type="text" placeholder="https://..." :class="{ 'input--error': fieldErrors.target_url }" />
          <small class="hint">Ruta donde ocurrio el error o donde se requiere ajuste.</small>
          <small v-if="fieldErrors.target_url" class="field-error">{{ fieldErrors.target_url }}</small>
        </label>

        <label class="field">
          <span>Prioridad</span>
          <select v-model="form.priority" :class="{ 'input--error': fieldErrors.priority }">
            <option value="sugerencia">Sugerencia</option>
            <option value="baja">Baja</option>
            <option value="media">Media</option>
            <option value="urgente">Urgente</option>
          </select>
          <small class="hint">Urgente requiere permiso especial.</small>
          <small v-if="fieldErrors.priority" class="field-error">{{ fieldErrors.priority }}</small>
        </label>

        <label class="field field--full">
          <span>Descripcion</span>
          <textarea v-model="form.description" rows="5" placeholder="Describe el error o solicitud" :class="{ 'input--error': fieldErrors.description }"></textarea>
          <small class="hint">Incluye detalle suficiente para analizar rapido.</small>
          <small v-if="fieldErrors.description" class="field-error">{{ fieldErrors.description }}</small>
        </label>
      </div>

      <div class="actions">
        <button class="btn btn--ghost" @click="setNow">Usar fecha/hora actual</button>
        <button class="btn btn--primary" :disabled="saving" @click="submitReport">
          {{ saving ? 'Enviando...' : 'Enviar reporte' }}
        </button>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
      <transition name="fade">
        <div v-if="success" class="toast">{{ success }}</div>
      </transition>
    </section>

    <section v-else class="card card--table">
      <div v-if="reportsError" class="error-banner" role="alert">
        <span>{{ reportsError }}</span>
        <button class="btn btn--ghost" @click="loadMyReports">Reintentar</button>
      </div>

      <div v-if="loadingReports" class="skeleton-table">
        <div v-for="n in 5" :key="n" class="skeleton-row"></div>
      </div>

      <div class="table-wrap" v-else-if="reports.length">
        <table class="table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Descripcion</th>
              <th>Prioridad</th>
              <th>Estatus</th>
              <th>Comentario admin</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="report in paginatedReports" :key="report.id">
              <td>{{ formatDate(report.created_at) }}</td>
              <td>
                <span>{{ shortText(report.description, report.id, 'description', 110) }}</span>
                <button
                  v-if="needsTruncate(report.description, 110)"
                  class="link-btn"
                  @click="toggleExpand(report.id, 'description')"
                >
                  {{ isExpanded(report.id, 'description') ? 'menos' : 'mas' }}
                </button>
              </td>
              <td><span :class="['priority-chip', priorityClass(report.priority)]">{{ report.priority_label || priorityLabel(report.priority) }}</span></td>
              <td><span :class="['status-chip', statusClass(report.status)]">{{ report.status_label || 'En revisión' }}</span></td>
              <td>
                <span>{{ shortText(report.admin_comment || 'Sin comentarios', report.id, 'comment', 100) }}</span>
                <button
                  v-if="needsTruncate(report.admin_comment || '', 100)"
                  class="link-btn"
                  @click="toggleExpand(report.id, 'comment')"
                >
                  {{ isExpanded(report.id, 'comment') ? 'menos' : 'mas' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="pagination" v-if="reports.length > pageSize">
          <button @click="prevPage" :disabled="currentPage === 1" class="pg-btn" aria-label="Página anterior">
            <img src="@/assets/angulo-izquierdo.png" alt="Previous Page" />
          </button>
          <span
            v-for="pageNumber in totalPages"
            :key="pageNumber"
            @click="goToPage(pageNumber)"
            :class="['pg-num', { active: pageNumber === currentPage }]"
          >
            {{ pageNumber }}
          </span>
          <button @click="nextPage" :disabled="currentPage >= totalPages" class="pg-btn" aria-label="Página siguiente">
            <img src="@/assets/angulo-derecho.png" alt="Next Page" />
          </button>
        </div>
      </div>
      <div v-else class="empty-state">
        <div class="empty-icon">🗂️</div>
        <p class="empty">Aun no tienes reportes creados.</p>
        <button class="btn btn--primary" @click="activeTab = 'form'">Crear mi primer reporte</button>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'BugReportUserPanel',
  data() {
    return {
      activeTab: 'form',
      username: '',
      permissions: [],
      reports: [],
      currentPage: 1,
      pageSize: 5,
      loadingReports: false,
      saving: false,
      error: '',
      reportsError: '',
      success: '',
      fieldErrors: {
        event_timestamp: '',
        target_url: '',
        description: '',
        priority: '',
      },
      expandedCells: {},
      form: {
        event_timestamp: '',
        target_url: '',
        description: '',
        priority: 'sugerencia',
      },
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.reports.length / this.pageSize))
    },
    paginatedReports() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.reports.slice(start, start + this.pageSize)
    },
  },
  created() {
    this.fetchCurrentUser()
    this.setNow()
  },
  methods: {
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    setNow() {
      const now = new Date()
      now.setSeconds(0, 0)
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hour = String(now.getHours()).padStart(2, '0')
      const minute = String(now.getMinutes()).padStart(2, '0')
      this.form.event_timestamp = `${year}-${month}-${day}T${hour}:${minute}`
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.username = data.username || ''
        this.permissions = data.permissions || []
        if (!this.hasPerm('maintenance.can_report_bug')) {
          this.$router.push({ name: 'MainPanel' })
        }
      } catch (error) {
        this.$router.push({ name: 'MainPanel' })
      }
    },
    async switchToMine() {
      this.activeTab = 'mine'
      await this.loadMyReports()
    },
    async loadMyReports() {
      const apiDirectory = process.env.API_DIRECTORY
      this.reportsError = ''
      this.loadingReports = true
      try {
        const { data } = await axios.get(`${apiDirectory}/bug-reports/mine`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.reports = Array.isArray(data) ? data : []
        this.currentPage = 1
      } catch (error) {
        this.reportsError = 'No se pudieron cargar tus reportes.'
      } finally {
        this.loadingReports = false
      }
    },
    clearFieldErrors() {
      this.fieldErrors = {
        event_timestamp: '',
        target_url: '',
        description: '',
        priority: '',
      }
    },
    validateForm() {
      this.clearFieldErrors()
      let isValid = true
      if (!this.form.event_timestamp) {
        this.fieldErrors.event_timestamp = 'La fecha y hora es obligatoria.'
        isValid = false
      }
      if (!this.form.target_url.trim()) {
        this.fieldErrors.target_url = 'La URL es obligatoria.'
        isValid = false
      }
      if (!this.form.description.trim()) {
        this.fieldErrors.description = 'La descripcion es obligatoria.'
        isValid = false
      }
      if (!this.form.priority) {
        this.fieldErrors.priority = 'La prioridad es obligatoria.'
        isValid = false
      }
      return isValid
    },
    async submitReport() {
      this.error = ''
      this.success = ''
      if (!this.validateForm()) {
        this.error = 'Completa todos los campos requeridos.'
        return
      }
      if (this.form.priority === 'urgente' && !this.hasPerm('maintenance.can_report_urgent_bug')) {
        this.error = 'No tienes permisos para crear reportes urgentes.'
        return
      }

      const apiDirectory = process.env.API_DIRECTORY
      this.saving = true
      try {
        await axios.post(
          `${apiDirectory}/bug-reports`,
          {
            event_timestamp: this.form.event_timestamp ? new Date(this.form.event_timestamp).toISOString() : null,
            target_url: this.form.target_url.trim(),
            description: this.form.description.trim(),
            priority: this.form.priority,
          },
          {
            headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
          }
        )
        this.success = 'Reporte enviado correctamente.'
        this.form.target_url = ''
        this.form.description = ''
        this.form.priority = 'sugerencia'
        this.setNow()
      } catch (error) {
        this.error = error?.response?.data?.error || 'No se pudo enviar el reporte.'
      } finally {
        this.saving = false
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('es-MX')
    },
    priorityLabel(value) {
      const map = {
        sugerencia: 'Sugerencia',
        baja: 'Baja',
        media: 'Media',
        urgente: 'Urgente',
      }
      return map[value] || 'Sugerencia'
    },
    priorityClass(value) {
      const map = {
        sugerencia: 'priority-sugerencia',
        baja: 'priority-baja',
        media: 'priority-media',
        urgente: 'priority-urgente',
      }
      return map[value] || 'priority-sugerencia'
    },
    statusClass(value) {
      const map = {
        en_revision: 'status-review',
        en_lista_espera: 'status-wait',
        rechazado: 'status-rejected',
        completado: 'status-done',
      }
      return map[value] || 'status-review'
    },
    goToPage(pageNumber) {
      this.currentPage = pageNumber
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage += 1
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage -= 1
    },
    cellKey(reportId, field) {
      return `${reportId}:${field}`
    },
    toggleExpand(reportId, field) {
      const key = this.cellKey(reportId, field)
      this.expandedCells = {
        ...this.expandedCells,
        [key]: !this.expandedCells[key],
      }
    },
    isExpanded(reportId, field) {
      return Boolean(this.expandedCells[this.cellKey(reportId, field)])
    },
    needsTruncate(value, maxChars) {
      return Boolean(value) && String(value).length > maxChars
    },
    shortText(value, reportId, field, maxChars) {
      const text = value || ''
      if (!this.needsTruncate(text, maxChars) || this.isExpanded(reportId, field)) {
        return text
      }
      return `${String(text).slice(0, maxChars)}...`
    },
    goMainPanel() {
      this.$router.push({ name: 'MainPanel' })
    },
    goBack() {
      this.$router.push({ path: '/ayuda' })
    },
  },
}
</script>

<style scoped>
.main-panel {
  --radius-lg: 16px;
  --radius-md: 10px;
  --line: #e5e7eb;
  --shadow-soft: 0 10px 20px rgba(15, 23, 42, 0.08);
  min-height: 100vh;
  background: #f3f6fd;
  padding: 12vh 24px 48px;
}
.nav-bar-operations { position: absolute; top: 0; left: 0; width: 100vw; height: 10vh; background: #ffc434; display: flex; align-items: center; padding-left: 16px; }
.logo-button { height: 55%; object-fit: contain; cursor: pointer; }
.header {
  margin-top: 10px;
  margin-bottom: 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: var(--radius-lg);
  padding: 12px 16px;
  backdrop-filter: blur(10px);
}
.header__left { display: flex; align-items: center; gap: 12px; }
.header__right { display: flex; align-items: center; gap: 8px; }
.avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #ffc434, #ffe188);
  color: #4d3600;
  font-weight: 800;
}
.header h1 { margin: 0; font-size: 1.2rem; color: #0f172a; }
.sub { margin: 3px 0 0; color: #667085; }
.hero-chip {
  border-radius: 999px;
  padding: 6px 11px;
  font-size: .76rem;
  font-weight: 700;
  color: #1e40af;
  background: rgba(37, 99, 235, 0.11);
  border: 1px solid rgba(37, 99, 235, 0.32);
}
.tabs { display: flex; gap: 8px; margin-bottom: 12px; }
.tab {
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.8);
  border-radius: var(--radius-md);
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.16s ease;
}
.tab--active {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
  box-shadow: 0 8px 16px rgba(37, 99, 235, 0.32);
}
.card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  padding: 16px;
  box-shadow: var(--shadow-soft);
}
.card--form {
  max-width: 980px;
  margin: 0 auto;
}
.card--table {
  max-width: 1240px;
  margin: 0 auto;
}
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.field { display: grid; gap: 6px; }
.field span { font-size: 0.95rem; font-weight: 600; color: #344054; }
.hint { color: #667085; font-size: .78rem; }
.field-error { color: #b42318; font-size: .78rem; font-weight: 600; }
.field--full { grid-column: 1 / -1; }
.field input, .field select, .field textarea {
  border: 1px solid #d0d7de;
  border-radius: var(--radius-md);
  padding: 9px 10px;
  font: inherit;
  transition: all 0.14s ease;
}
.field input:focus, .field select:focus, .field textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
}
.input--error { border-color: #f04438 !important; box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.14) !important; }
.actions { display: flex; justify-content: space-between; margin-top: 12px; }
.btn { border-radius: var(--radius-md); padding: 9px 12px; border: 1px solid transparent; cursor: pointer; transition: all 0.16s ease; }
.btn--ghost { background: #fff; border-color: #d0d7de; }
.btn--primary { background: #2563eb; color: #fff; }
.btn--primary:hover { box-shadow: 0 10px 18px rgba(37, 99, 235, 0.34); }
.btn:focus { outline: none; box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.2); }
.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 1rem;
}
.table th, .table td {
  text-align: left;
  border-bottom: 1px solid var(--line);
  padding: 12px 11px;
  vertical-align: top;
}
.table th {
  color: #344054;
  font-size: 0.95rem;
  font-weight: 800;
  background: #f8faff;
  position: sticky;
  top: 0;
  z-index: 1;
}
.table-wrap {
  max-height: 460px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
}
.table tbody tr:nth-child(even) td { background: #fcfdff; }
.table th:nth-child(3), .table td:nth-child(3),
.table th:nth-child(4), .table td:nth-child(4) { text-align: center; white-space: nowrap; }
.table td {
  color: #101828;
  line-height: 1.45;
}
.empty { color: #667085; margin: 0; }
.error { color: #b42318; margin-top: 10px; }
.error-banner {
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(240, 68, 56, 0.35);
  background: rgba(254, 228, 226, 0.8);
  color: #912018;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.empty-state {
  min-height: 260px;
  border: 1px dashed #d0d7de;
  border-radius: 12px;
  display: grid;
  place-items: center;
  text-align: center;
  gap: 8px;
  padding: 20px;
}
.empty-icon { font-size: 2rem; }
.link-btn {
  border: 0;
  background: transparent;
  color: #1d4ed8;
  cursor: pointer;
  font-size: .8rem;
  font-weight: 700;
  margin-left: 6px;
}
.toast {
  margin-top: 10px;
  border: 1px solid rgba(22, 163, 74, 0.35);
  background: rgba(220, 252, 231, 0.85);
  color: #166534;
  border-radius: 10px;
  padding: 9px 12px;
  font-weight: 600;
}
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.skeleton-table { display: grid; gap: 8px; }
.skeleton-row {
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(100deg, #eef2ff 20%, #f8fbff 50%, #eef2ff 80%);
  background-size: 220% 100%;
  animation: shimmer 1.2s infinite linear;
}
@keyframes shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
.priority-chip, .status-chip {
  display: inline-flex;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: .76rem;
  font-weight: 700;
  border: 1px solid transparent;
}
.priority-sugerencia { background: rgba(37, 99, 235, 0.1); color: #1e40af; border-color: rgba(37, 99, 235, 0.3); }
.priority-baja { background: rgba(8, 145, 178, 0.12); color: #155e75; border-color: rgba(8, 145, 178, 0.35); }
.priority-media { background: rgba(217, 119, 6, 0.12); color: #92400e; border-color: rgba(217, 119, 6, 0.35); }
.priority-urgente { background: rgba(220, 38, 38, 0.14); color: #b91c1c; border-color: rgba(220, 38, 38, 0.38); }
.status-review { background: rgba(37, 99, 235, 0.1); color: #1e40af; border-color: rgba(37, 99, 235, 0.3); }
.status-wait { background: rgba(217, 119, 6, 0.12); color: #92400e; border-color: rgba(217, 119, 6, 0.35); }
.status-rejected { background: rgba(220, 38, 38, 0.12); color: #b91c1c; border-color: rgba(220, 38, 38, 0.35); }
.status-done { background: rgba(22, 163, 74, 0.13); color: #166534; border-color: rgba(22, 163, 74, 0.35); }
.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 12px 8px 4px;
}
.pg-btn {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 2px 6px;
  cursor: pointer;
}
.pg-btn:disabled { opacity: .5; cursor: not-allowed; }
.pg-btn img { width: 18px; height: auto; display: block; }
.pg-num {
  min-width: 30px;
  text-align: center;
  padding: 5px 8px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
}
.pg-num.active {
  background: #ffc434;
  border-color: #e0b12b;
}
@media (max-width: 900px) {
  .form-grid { grid-template-columns: 1fr; }
  .header { flex-direction: column; align-items: flex-start; }
  .header__right { width: 100%; justify-content: space-between; }
  .table-wrap { max-height: none; overflow: visible; }
}
@media (min-width: 1400px) {
  .card--form {
    max-width: 900px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .tab,
  .btn,
  .field input,
  .field select,
  .field textarea,
  .fade-enter-active,
  .fade-leave-active,
  .skeleton-row {
    transition: none;
    animation: none;
  }
}
</style>
